import React, { useState } from 'react';

import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  styled,
  Alert,
  CircularProgress,
} from '@mui/material';

import Facebook from '../assets/images/facebook-icon.png';
import Telegram from '../assets/images/telegram-icon.png';
import Viber from '../assets/images/viber-icon.png';
import YouTube from '../assets/images/youtube-icon.png';


// ===============================
// API BASE URL
// ===============================

// Example:
// http://localhost:3000/client/contact
//
// Environment variable ရှိရင် အဲ့ဒါကိုသုံးမယ်

const API_BASE_URL = "https://supermobile.com.mm/client/api-v1"


// ===============================
// Styled Components
// ===============================

const ContactPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  display: 'flex',
  boxShadow: '0px 10px 30px rgba(0,0,0,0.1)',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));


const FormTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: '#F8FAFC',
    borderRadius: '4px',

    '&:before, &:after': {
      borderBottom: 'none !important',
    },

    padding: '12px',
  },

  '& .MuiInputLabel-root': {
    color: '#3A3A3C',
    fontWeight: 400,
    fontSize: '16px',
    marginBottom: '4px',
  },

  '& .MuiInputBase-input::placeholder': {
    color: '#A0A0A0',
    opacity: 1,
  },
}));


const SocialIconCircle = styled(Box)(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',

  '&:hover': {
    opacity: 0.8,
  },
}));


// ===============================
// Contact Us
// ===============================

const ContactUs = () => {


  // ===============================
  // Form State
  // ===============================

  const [formData, setFormData] = useState({

    first_name: '',

    last_name: '',

    phone: '',

    email: '',

    message: '',

  });


  // ===============================
  // Loading State
  // ===============================

  const [loading, setLoading] =
    useState(false);


  // ===============================
  // Success / Error Message
  // ===============================

  const [successMessage, setSuccessMessage] =
    useState('');

  const [errorMessage, setErrorMessage] =
    useState('');



  // ===============================
  // Handle Input Change
  // ===============================

  const handleChange = (event) => {

    const {
      name,
      value,
    } = event.target;


    setFormData((prev) => ({

      ...prev,

      [name]: value,

    }));

  };



  // ===============================
  // Submit Contact Form
  // ===============================

  const handleSubmit = async (event) => {

    event.preventDefault();


    // Reset Messages

    setSuccessMessage('');

    setErrorMessage('');


    // ===============================
    // Frontend Validation
    // ===============================

    if (!formData.first_name.trim()) {

      setErrorMessage(
        'Please enter your first name.'
      );

      return;

    }


    if (!formData.email.trim()) {

      setErrorMessage(
        'Please enter your email.'
      );

      return;

    }


    if (!formData.message.trim()) {

      setErrorMessage(
        'Please enter your message.'
      );

      return;

    }


    try {

      setLoading(true);


      // ===============================
      // API CALL
      // ===============================

      const response = await fetch(

        `${API_BASE_URL}/contact`,

        {

          method: 'POST',

          headers: {

            'Content-Type':
              'application/json',

          },

          body: JSON.stringify({

            first_name:
              formData.first_name,

            last_name:
              formData.last_name,

            phone:
              formData.phone,

            email:
              formData.email,

            message:
              formData.message,

          }),

        }

      );


      const data =
        await response.json();


      // ===============================
      // API ERROR
      // ===============================

      if (!response.ok) {

        throw new Error(

          data.message ||
          'Failed to send message.'

        );

      }


      // ===============================
      // SUCCESS
      // ===============================

      if (data.success) {

        setSuccessMessage(

          data.message ||
          'Your message has been sent successfully!'

        );


        // Clear Form

        setFormData({

          first_name: '',

          last_name: '',

          phone: '',

          email: '',

          message: '',

        });

      }

    } catch (error) {

      console.error(
        'Contact Form Error:',
        error
      );


      setErrorMessage(

        error.message ||
        'Something went wrong. Please try again.'

      );

    } finally {

      setLoading(false);

    }

  };



  return (

    <Box
      sx={{
        py: 10,
        px: {
          xs: 2,
          md: 10,
        },
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#F0F2F5',
      }}
    >

      <ContactPaper elevation={0}>


        {/* ===============================
            LEFT SIDE - CONTACT FORM
        =============================== */}

        <Box
          sx={{
            py: 2,
            px: 3,
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
        >


          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: '#D32F2F',
              fontWeight: 'bold',
              mb: 1,
            }}
          >

            Send a Message

          </Typography>


          <Typography
            variant="body1"
            sx={{
              color: '#000000',
              mb: 4,
            }}
          >

            Any question or remarks? Let us know!

          </Typography>



          {/* ===============================
              SUCCESS MESSAGE
          =============================== */}

          {successMessage && (

            <Alert
              severity="success"
              sx={{
                mb: 3,
              }}
            >

              {successMessage}

            </Alert>

          )}



          {/* ===============================
              ERROR MESSAGE
          =============================== */}

          {errorMessage && (

            <Alert
              severity="error"
              sx={{
                mb: 3,
              }}
            >

              {errorMessage}

            </Alert>

          )}



          {/* ===============================
              CONTACT FORM
          =============================== */}

          <Box
            component="form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >


            <Grid
              container
              spacing={3}
            >


              {/* First Name */}

              <Grid
                item
                xs={12}
                sm={6}
              >

                <FormTextField

                  fullWidth

                  label="First Name"

                  placeholder="First Name"

                  variant="standard"

                  InputLabelProps={{
                    shrink: true,
                  }}

                  name="first_name"

                  value={
                    formData.first_name
                  }

                  onChange={
                    handleChange
                  }

                  disabled={
                    loading
                  }

                />

              </Grid>



              {/* Last Name */}

              <Grid
                item
                xs={12}
                sm={6}
              >

                <FormTextField

                  fullWidth

                  label="Last Name"

                  placeholder="Last Name"

                  variant="standard"

                  InputLabelProps={{
                    shrink: true,
                  }}

                  name="last_name"

                  value={
                    formData.last_name
                  }

                  onChange={
                    handleChange
                  }

                  disabled={
                    loading
                  }

                />

              </Grid>



              {/* Phone */}

              <Grid
                item
                xs={12}
                sm={6}
              >

                <FormTextField

                  fullWidth

                  label="Phone"

                  placeholder="09xxxxxxxxx"

                  variant="standard"

                  InputLabelProps={{
                    shrink: true,
                  }}

                  name="phone"

                  value={
                    formData.phone
                  }

                  onChange={
                    handleChange
                  }

                  disabled={
                    loading
                  }

                />

              </Grid>



              {/* Email */}

              <Grid
                item
                xs={12}
                sm={6}
              >

                <FormTextField

                  fullWidth

                  label="Email"

                  placeholder="example@gmail.com"

                  type="email"

                  variant="standard"

                  InputLabelProps={{
                    shrink: true,
                  }}

                  name="email"

                  value={
                    formData.email
                  }

                  onChange={
                    handleChange
                  }

                  disabled={
                    loading
                  }

                />

              </Grid>



              {/* Message */}

              <Grid item xs={12}>

                <FormTextField

                  fullWidth

                  label="Message"

                  placeholder="Write your message"

                  variant="standard"

                  multiline

                  rows={4}

                  InputLabelProps={{
                    shrink: true,
                  }}

                  name="message"

                  value={
                    formData.message
                  }

                  onChange={
                    handleChange
                  }

                  disabled={
                    loading
                  }

                />

              </Grid>


            </Grid>



            {/* ===============================
                SOCIAL ICONS + SEND BUTTON
            =============================== */}

            <Stack

              direction="row"

              justifyContent="space-between"

              alignItems="center"

              sx={{
                mt: 5,
              }}

            >


              {/* Social Icons */}

              <Stack
                direction="row"
                spacing={1}
              >


                <SocialIconCircle>

                  <Box

                    component="img"

                    src={Facebook}

                    width="28px"

                    height="28px"

                    alt="Facebook"

                  />

                </SocialIconCircle>



                <SocialIconCircle>

                  <Box

                    component="img"

                    src={Telegram}

                    width="23px"

                    height="23px"

                    alt="Telegram"

                  />

                </SocialIconCircle>



                <SocialIconCircle>

                  <Box

                    component="img"

                    src={YouTube}

                    width="30px"

                    height="30px"

                    alt="YouTube"

                  />

                </SocialIconCircle>



                <SocialIconCircle>

                  <Box

                    component="img"

                    src={Viber}

                    width="26px"

                    height="26px"

                    alt="Viber"

                  />

                </SocialIconCircle>


              </Stack>



              {/* ===============================
                  SEND BUTTON
              =============================== */}

              <Button

                type="submit"

                variant="contained"

                disabled={
                  loading
                }

                sx={{

                  backgroundColor:
                    '#D32F2F',

                  borderRadius:
                    '8px',

                  textTransform:
                    'none',

                  padding:
                    '10px 24px',

                  minWidth:
                    '160px',

                  '&:hover': {

                    backgroundColor:
                      '#B71C1C',

                  },

                }}

              >

                {loading ? (

                  <CircularProgress
                    size={22}
                    color="inherit"
                  />

                ) : (

                  'Send a message'

                )}

              </Button>


            </Stack>


          </Box>


        </Box>



        {/* ===============================
            RIGHT SIDE - GOOGLE MAP
        =============================== */}

        <Box

          sx={{

            width: {
              xs: '100%',
              md: '50%',
            },

            minHeight: {
              xs: '400px',
              md: 'auto',
            },

          }}

        >

          <iframe

            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3699.79107987197!2d96.08434207435018!3d21.980979979917393!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30cb6d2067c28507%3A0x3645f011eec77ee1!2sSUPER%20MOBILE%20(%20Mobile%20Sales%20%26%20Service%20Center%20)!5e0!3m2!1sen!2smm!4v1785074385934!5m2!1sen!2smm"

            width="100%"

            height="100%"

            style={{
              border: 0,
            }}

            allowFullScreen

            loading="lazy"

            referrerPolicy="strict-origin-when-cross-origin"

            title="Super Mobile Location"

          />

        </Box>


      </ContactPaper>

    </Box>

  );

};


export default ContactUs;